import React, { useMemo, useState, useImperativeHandle, forwardRef, useCallback } from 'react';
import { TaskItem } from '@demind-inc/core';
import { AddOutlined } from '@mui/icons-material';

import './AddSubTask.scss';
import { AddTodoTaskItemProps } from '../../../../data-access';
import { SubTaskItem } from '../SubTaskItem';
import { Typography } from '@mui/material';
import clsx from 'clsx';
import { CustomTooltip } from '../../../common';
import { useTranslation } from 'react-i18next';

interface AddSubTaskProps {
  parentTask: TaskItem;
  subtasksCount: number;
  hideDragHandle?: boolean;
  onCreateSubTask?: (options: AddTodoTaskItemProps) => Promise<void>;
  onEditStateChange?: (isEditing: boolean) => void;
}

export interface AddSubTaskRef {
  startCreating: () => void;
}

const AddSubTask = forwardRef<AddSubTaskRef, AddSubTaskProps>(
  (
    {
      parentTask,
      subtasksCount,
      hideDragHandle = false,
      onCreateSubTask = () => {},
      onEditStateChange,
    },
    ref
  ) => {
    const { t } = useTranslation();
    const [openEditItem, setOpenEditItem] = useState(false);
    const [tempTaskId, setTempTaskId] = useState<string>('');
    const isSynced = useMemo(() => !!parentTask.originalTaskId, [parentTask.originalTaskId]);

    const handleStartCreating = useCallback(() => {
      if (!isSynced) return;
      const tempId = `temp-${Date.now()}`;
      setTempTaskId(tempId);
      setOpenEditItem(true);
      onEditStateChange?.(true);
    }, [isSynced, onEditStateChange]);

    useImperativeHandle(
      ref,
      () => ({
        startCreating: handleStartCreating,
      }),
      [handleStartCreating]
    );

    const handleCreateSubTask = async (taskName: string) => {
      if (!taskName.trim()) return;
      try {
        const newSubTask: AddTodoTaskItemProps = {
          name: taskName.trim(),
          parentOriginalTaskId: parentTask.originalTaskId,
          childOrder: subtasksCount,
          dueDateTime: parentTask.dueDateTime,
          boardId: parentTask.boardId!,
          appFrom: parentTask.appFrom,
          sortIndex: parentTask.sortIndex,
          completed: false,
          updatedAt: new Date().toISOString(),
        };

        onCreateSubTask(newSubTask);
        setTempTaskId('');
        setOpenEditItem(false);
        onEditStateChange?.(false);
      } catch (error) {
        console.error('Failed to create subtask:', error);
      } finally {
      }
    };

    const handleCancel = () => {
      setOpenEditItem(false);
      setTempTaskId('');
      onEditStateChange?.(false);
    };

    if (openEditItem) {
      const tempTask: TaskItem = {
        taskId: tempTaskId,
        originalTaskId: tempTaskId,
        name: '',
        appFrom: parentTask.appFrom,
        updatedAt: new Date().toISOString(),
        parentOriginalTaskId: parentTask.originalTaskId,
        childOrder: subtasksCount,
        dueDateTime: parentTask.dueDateTime,
        boardId: parentTask.boardId!,
        sortIndex: parentTask.sortIndex,
        completed: false,
      };

      return (
        <SubTaskItem
          task={tempTask}
          onUpdateSubTask={(taskId, updates) => {
            if (updates.name?.trim()) {
              handleCreateSubTask(updates.name);
            }
          }}
          disableDrag={hideDragHandle}
          onCancel={handleCancel}
          isEditing
          isNewTask
          className="add-subtask__item"
        />
      );
    }

    return (
      <CustomTooltip
        title={
          isSynced ? (
            ''
          ) : (
            <Typography variant="subtitle2" className="add-subtask__tooltip-text">
              {t('task.subtasks.waitForSync')}
            </Typography>
          )
        }
        placement="top"
      >
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleStartCreating();
          }}
          className={clsx('add-subtask__btn', {
            'add-subtask--disabled': !isSynced,
          })}
          disabled={!isSynced}
        >
          <span className="add-subtask__btn__text">{t('task.subtasks.addSubtask')}</span>
          <AddOutlined className="add-subtask__btn__icon" />
        </button>
      </CustomTooltip>
    );
  }
);

AddSubTask.displayName = 'AddSubTask';

export default AddSubTask;
